import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, Clock, Mail, Phone, Search, UserRound, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';

const AdminUsers = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load users', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => (
      user.name?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q) ||
      user.phone?.toLowerCase().includes(q)
    ));
  }, [query, users]);

  const verifiedCount = users.filter((user) => user.is_verified).length;
  const activeCount = users.filter((user) => user.is_active !== false).length;

  return (
    <div data-testid="admin-users-page">
      <div className="mb-6">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Registered Users</h2>
        <p className="text-gray-600 mt-1">
          {users.length} customer{users.length !== 1 ? 's' : ''} registered
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Users</div>
            <div className="text-2xl font-bold text-gray-900">{users.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Verified Emails</div>
            <div className="text-2xl font-bold text-green-600">{verifiedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Active Accounts</div>
            <div className="text-2xl font-bold text-blue-600">{activeCount}</div>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name, email, or phone"
          className="pl-10"
          data-testid="users-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <UserRound className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">{users.length === 0 ? 'No users registered yet' : 'No users match your search'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-600">Customer</th>
                  <th className="text-left p-3 font-semibold text-gray-600">Contact</th>
                  <th className="text-left p-3 font-semibold text-gray-600">Email Status</th>
                  <th className="text-left p-3 font-semibold text-gray-600">Account</th>
                  <th className="text-left p-3 font-semibold text-gray-600">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="p-3">
                      <div className="font-semibold text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{user.id}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 text-gray-900">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <a href={`mailto:${user.email}`} className="hover:text-red-600">{user.email}</a>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 mt-1">
                        <Phone className="h-4 w-4 text-gray-400" />
                        {user.phone || 'N/A'}
                      </div>
                    </td>
                    <td className="p-3">
                      {user.is_verified ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />Verified
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-700">
                          <Clock className="h-3 w-3 mr-1" />Pending
                        </Badge>
                      )}
                    </td>
                    <td className="p-3">
                      {user.is_active === false ? (
                        <Badge className="bg-red-100 text-red-700">
                          <XCircle className="h-3 w-3 mr-1" />Disabled
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-700">Active</Badge>
                      )}
                    </td>
                    <td className="p-3 text-gray-600">
                      {user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
